import { Portal } from '@radix-ui/react-portal'
import { Slot } from '@radix-ui/react-slot'
import clsx from 'clsx'
import { XIcon } from 'lucide-react'
import React, { forwardRef, useEffect, useId, useState } from 'react'

import { Presence } from 'slab'

interface BottomSheetProps {
  trigger?: React.ReactNode;
  showCloseButton?: boolean;
  children?:
    | React.ReactNode
    | (({ dismissSheet }: { dismissSheet: () => void }) => React.ReactNode);
  zIndex?: number;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  onClose?: () => void;
}

export const BottomSheet = forwardRef<HTMLDivElement, BottomSheetProps>(({ trigger, showCloseButton = false, children, zIndex, open: controlledOpen, onOpenChange, onClose }, forwardedRef) => {
  const [uncontrolledOpen, setUncontrolledOpen] = useState(false)
  const id = useId()

  // Use controlled state if provided, otherwise use internal state
  const isControlled = controlledOpen !== undefined
  const isOpen = isControlled ? controlledOpen : uncontrolledOpen

  const setIsOpen = (newOpen: boolean) => {
    if (isControlled) {
      onOpenChange?.(newOpen)
    } else {
      setUncontrolledOpen(newOpen)
    }
  }

  useEffect(() => {
    if (isOpen) {
      const originalOverflow = document.body.style.overflow
      document.body.style.overflow = 'hidden'

      return () => {
        document.body.style.overflow = originalOverflow
      }
    }
  }, [isOpen])

  function renderChildren () {
    if (typeof children === 'function') {
      return children({
        dismissSheet: () => {
          setIsOpen(false)
          onClose?.()
        }
      })
    }

    return children
  }

  return (
    <>
      {trigger && (
        <Slot
          aria-controls={id}
          aria-haspopup='dialog'
          aria-expanded={isOpen}
          onClick={() => {
            setIsOpen(true)
          }}
        >
          {trigger}
        </Slot>
      )}
      <Portal>
        <div
          id={id}
          className={clsx('fixed inset-0 transition duration-300', {
            'pointer-events-none bg-black/0': !isOpen,
            'bg-black/40': isOpen,
          })}
          onClick={() => {
            setIsOpen(false)
            onClose?.()
          }}
          role='dialog'
          style={{ zIndex }}
        />
        <div
          className={clsx(
            'fixed bottom-0 left-0 right-0 bg-white rounded-t-2xl transition-all duration-300 overflow-hidden',
            {
              'h-0 invisible': !isOpen,
              'h-4/5': isOpen,
            }
          )}
          style={{ zIndex }}
        >
          <Presence asChild present={isOpen}>
            <div
              className={clsx('relative h-full transition duration-300', {
                'opacity-0': !isOpen,
                'opacity-100': isOpen,
              })}
            >
              {showCloseButton && (
                <button
                  className='absolute top-4 right-4 w-8 h-8 bg-white rounded-full'
                  onClick={() => {
                    setIsOpen(false)
                    onClose?.()
                  }}
                  aria-label='Close sheet'
                >
                  <XIcon className='self-center justify-self-center w-6 h-6 ' />
                </button>
              )}
              <div className='flex flex-col h-full overflow-y-auto overscroll-contain' ref={forwardedRef}>
                {renderChildren()}
              </div>
            </div>
          </Presence>
        </div>
      </Portal>
    </>
  )
})
